import { Injectable } from '@angular/core';

import {
  asyncScheduler,
  BehaviorSubject,
  combineLatest,
  Observable,
} from 'rxjs';
import {
  map,
  tap,
  startWith,
  filter,
  debounceTime,
  shareReplay,
} from 'rxjs/operators';

import {
  ProfileCollectionItem,
  CollectionItem,
  CollectionItemType,
  CollectionFacet,
} from '@app/profile/profile-api.model';
import { ProfileCollectionService } from '../services/profile-collection.service';

import { InputsService } from '@app/inputs/services/inputs.service';
import { UserOutcomeService } from '@app/outcomes/services/user-outcome.service';
import { ModalService } from '@app/shared/services/modal.service';
import { NotifierService } from '@app/shared/services/notifier.service';

import { Profile } from '@app/profile/profile.model';
import { TagsApi } from '@app/tags/tag-api.model';

import { TrackerService } from '@app/shared/services/tracker.service';
import { DatePipe } from '@angular/common';

import { cloneDeep as _cloneDeep } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';
import { SimpleModalComponent } from '@app/shared/components/modal/simple-modal/simple-modal.component';
import { ProfileCollectionBulkUpdateModal } from '@app/profile/components/profile-collection/bulk-update/update-modal/profile-collection-bulk-update-modal.component';
import { LearningResourceViewModel } from '@app/inputs/models/learning-resource.view-model';
import { ProfileOverviewActionsService } from '@app/profile/components/profile-overview/profile-overview-actions/profile-overview-actions.service';
import { LDFlagsService } from '@dg/shared-services';
import { ProfileService } from '@app/profile/services/profile.service';
import { getEmptyTileCount } from '@app/profile/components/profile-overview-v2/profile-overview.utils';
import { UserOutcomeActionsService } from '@app/profile/components/user-outcome-tile-v2/user-outcome-actions/user-outcome-actions.service';
import {
  SortKey,
  CollectionFilterV2,
  ProfileCollectionViewModel,
  ProfileCollectionStoreState,
  ProfileCollectionState,
  BulkUpdateVM,
} from '@app/profile/components/profile-collection/profile-collection-reactive-store';
import { Entity, ReactiveStore } from '@dg/shared-rsm';
import {
  isPlanOrPathwayTile,
  isInputTile,
  isExperienceTile,
  isOutcomeTile,
  isItemSelected,
  getItemTypes,
  getDisplayFacets,
  getItemForModel,
  getBulkUpdateEmptyState,
  getFilterEmptyState,
  ProfileCollectionUtilsService,
  ITEM_TYPE_MAP,
} from '@app/profile/components/profile-collection/profile-collection-reactive-store/utils';

import { HttpErrorResponse } from '@angular/common/http';
import { readFirst } from '@dg/shared-rxjs';

const TILE_ROW_LIMIT = 4;

const initStore = (): ProfileCollectionStoreState => ({
  items: [],
  processedFacets: { itemTypes: [], facets: [] },
  displayFacets: [],
  hasMoreItems: false,
  filter: {
    tags: [],
    providers: [],
    pathwayStatus: [],
    targetStatus: [],
    type: [],
    term: null,
    currentItemClassifier: 'learning',
    experienceType: [],
  },
  bulkUpdateVM: {
    enabled: false,
    selectedItems: [],
    deleteModalHeaderText: '',
    deleteModalBodyText: '',
    clearSelectionText: '',
    selectedItemsText: '',
  },
  status: {
    appending: false,
    filterUpdating: false,
    loading: false,
  },
  indexesOfCardsToExpand: [],
});

@Injectable()
export class ProfileCollectionFacade {
  /**
   * Scheduler used for `debounceTime()` for the profile collection
   * This allows tests to override with a virtual scheduler
   */
  public static debounceScheduler = asyncScheduler;

  public vm$: Observable<ProfileCollectionViewModel>;
  // PD-93836 check
  private _hasError$: BehaviorSubject<boolean>;

  private _reload$ = new BehaviorSubject<boolean>(true);
  private _enabled = true;
  private _store: ReactiveStore<ProfileCollectionStoreState, Entity>;

  constructor(
    private _api: ProfileCollectionService,
    private inputsService: InputsService,
    private userOutcomeService: UserOutcomeService,
    private trackerService: TrackerService,
    private translateService: TranslateService,
    private datePipe: DatePipe,
    private modalService: ModalService,
    private notifierService: NotifierService,
    private profileOverviewActionsService: ProfileOverviewActionsService,
    private profileService: ProfileService,
    private userOutcomeActionsService: UserOutcomeActionsService,
    private ldFlagsService: LDFlagsService,
    private profileCollectionUtilsService: ProfileCollectionUtilsService
  ) {
    this._store = new ReactiveStore<ProfileCollectionStoreState, Entity>(
      'profileCollection',
      initStore
    );

    this.vm$ = this._store.state$.pipe(
      map(this.addComputedProperties.bind(this)),
      map(this.addViewModelAPI.bind(this)),
      //   map(this.trackProfileView.bind(this)),
      shareReplay<ProfileCollectionViewModel>({
        refCount: false,
        bufferSize: 1,
      })
    );

    this._hasError$ = new BehaviorSubject<boolean>(false);

    // The triggers below are a basic WIP so that filtering on enabled could easily be added.
    // We've added https://degreedjira.atlassian.net/browse/PD-93319 for the 2024.0.1.0 sprint to start addressing this facade.
    const dataTriggers$ = combineLatest([
      this.userOutcomeService.userOutcomeModify.pipe(startWith(null)),
      this.inputsService.inputModify.pipe(startWith(null)),
    ]).pipe(
      filter((args) => {
        // ignore initial values
        return this.enabled && args.some((v) => !!v);
      }),
      debounceTime(20, ProfileCollectionFacade.debounceScheduler)
    );

    this.updateDataWhen(dataTriggers$);

    this.profileOverviewActionsService.inputSelectedSubject$
      .pipe(filter(() => this.enabled))
      .subscribe((model: LearningResourceViewModel) => {
        const item = getItemForModel(model, this.items);
        this.toggleItemSelection(item);
      });

    this.userOutcomeActionsService.itemSelected
      .pipe(filter(() => this.enabled))
      .subscribe((item) => {
        this.toggleItemSelection(item);
      });
  }

  /**
   * Special property to either force reload or IGNORE other triggers
   * Use to temporarily disable the facade when not needed
   */
  public get enabled(): boolean {
    return this._enabled;
  }

  public set enabled(value: boolean) {
    if (value === this._enabled) return;
    this._enabled = value;

    // Force trigger$ emission to refresh the profile VM
    if (!!value) this._reload$.next(true);
  }

  public get filter(): CollectionFilterV2 {
    return this._store.useQuery((s) => s.filter);
  }

  public get bulkUpdateVM(): BulkUpdateVM {
    return this._store.useQuery((s) => s.bulkUpdateVM);
  }

  public get items(): ProfileCollectionItem<any>[] {
    return this._store.useQuery((s) => s.items);
  }

  public get status(): {
    appending: boolean;
    filterUpdating: boolean;
  } {
    return this._store.useQuery((s) => s.status);
  }

  public get hasError$(): Observable<boolean> {
    return this._hasError$.asObservable();
  }

  public get pathwaysOrPlansDisplayed(): boolean {
    return ['pathways', 'plans'].includes(this.filter.currentItemClassifier);
  }

  public get owner(): Profile {
    return this.profileService.profileInput?.profile;
  }

  // @todo make this and onFiltersChanged more generic so that we don't need to update everytime a new filter is added
  public get hasFilterValues(): boolean {
    const {
      tags,
      type,
      term,
      providers,
      startDate,
      endDate,
      pathwayStatus,
      targetStatus,
      experienceType,
    } = this.filter;
    return (
      !!tags.length ||
      !!type.length ||
      !!term?.length ||
      !!providers.length ||
      !!startDate ||
      !!endDate ||
      !!pathwayStatus.length ||
      !!targetStatus.length ||
      !!experienceType.length
    );
  }

  private updateDataWhen(triggers$: Observable<unknown>): void {
    // !! do not cache the `unsubscribe()` as this is a permanent connection
    triggers$.subscribe(() => readFirst(this.getCollectionItems(false)));
  }

  public setSortKey(sortKey: SortKey): void {
    this.updateState({ sorting: sortKey });
    readFirst(this.getCollectionItems(false));
  }

  public showBulkDeleteModal(): void {
    this.modalService
      .show<any>(SimpleModalComponent, {
        inputs: {
          canCancel: true,
          bodyClasses: 'center-text',
          headerText: this.bulkUpdateVM.deleteModalHeaderText,
          bodyText: this.bulkUpdateVM.deleteModalBodyText,
          submitButtonText: this.translateService.instant('Core_Delete'),
        },
      })
      .subscribe(() => {
        const numItems = this.bulkUpdateVM.selectedItems.length;
        this.deleteSelectedCollectionItems().subscribe(() => {
          this.resetBulkUpdate();
          this.notifierService.showSuccess(
            this.translateService.instant(
              'ProfileCtrl_DeleteCollectionMultipleItemsNotifierFormat',
              {
                selectedLength: numItems,
              }
            )
          );
        });
      });
  }

  public showBulkUpdateModal(): void {
    const inputs = { tags: [] };
    this.modalService
      .show(ProfileCollectionBulkUpdateModal, {
        inputs,
      })
      .subscribe((updatedTags: TagsApi.Tag[]) => {
        const numItems = this.bulkUpdateVM.selectedItems.length.toString();
        this.updateSelectedCollectionItems(updatedTags).subscribe(() => {
          this.resetBulkUpdate();
          this.notifierService.showSuccess(
            this.translateService.instant(
              'ProfileCtrl_EditCollectionMultipleItemsNotifierFormat',
              {
                selectedLength: numItems,
              }
            )
          );
        });
      });
  }

  public toggleBulkEditMode(): void {
    this.bulkUpdateVM.enabled ? this.resetBulkUpdate() : this.setBulkEditMode();
  }

  public setBulkEditMode(
    selectedItems?: ProfileCollectionItem<any>[],
    enabled: boolean = true
  ): void {
    const items = selectedItems || this.bulkUpdateVM.selectedItems;
    this.updateState(
      this.profileCollectionUtilsService.getBulkEditState(items, enabled)
    );
  }

  public toggleItemSelection(collectionItem: ProfileCollectionItem<any>): void {
    const bulkUpdateVM = this.bulkUpdateVM;
    if (!bulkUpdateVM.enabled) {
      this.toggleBulkEditMode();
    }
    const selected = !bulkUpdateVM.selectedItems.find(
      (item) => item.userItemId === collectionItem.userItemId
    );
    const selectedItems = selected
      ? [...bulkUpdateVM.selectedItems, collectionItem]
      : bulkUpdateVM.selectedItems.filter(
          (item) => item.userItemId !== collectionItem.userItemId
        );
    this.setBulkEditMode(selectedItems, !!selectedItems.length);
  }

  public deleteSelectedCollectionItems(): Observable<any> {
    return this._api
      .deleteCollectionItems(this.bulkUpdateVM.selectedItems)
      .pipe(
        tap(() => {
          this.resetBulkUpdate();
          readFirst(this.getCollectionItems(false));
        })
      );
  }

  public resetBulkUpdate(): void {
    this.updateState({
      bulkUpdateVM: getBulkUpdateEmptyState(),
    });
  }

  public searchItems(searchTerm: string): void {
    this.updateFilter('term', searchTerm);
    readFirst(this.getCollectionItems(false, true));
    this.trackSearch();
  }

  public searchFacets(facetId: string, searchTerm: string) {
    const filterValueMap = {
      tags: 'id',
      providers: 'name',
      experienceType: 'id',
    };

    const { processedFacets, displayFacets } = this._store.useQuery((s) => s);
    const baseFacet = processedFacets.facets.find(
      (facet: CollectionFacet) =>
        facet.id.toLowerCase() === facetId.toLowerCase()
    );

    const newDisplayFacets = _cloneDeep(displayFacets);
    const displayFacet = newDisplayFacets.find(
      (f: CollectionFacet) => f.id.toLowerCase() === facetId.toLowerCase()
    );

    if (!searchTerm) {
      displayFacet.values = _cloneDeep(baseFacet.values);
    } else {
      displayFacet.values = baseFacet.values.filter((v: CollectionFacet) =>
        v[filterValueMap[facetId]]
          .toLowerCase()
          .startsWith(searchTerm.toLowerCase())
      );
    }
    this.updateState({ displayFacets: newDisplayFacets });
  }

  public updateDateFilterValues(startDate: string, endDate: string) {
    const newFilter = {
      ...this.filter,
      startDate,
      endDate,
    };
    this.updateState({ filter: newFilter, filterToFocus: 'date' });
    readFirst(this.getCollectionItems(false, true));

    this.trackFiltering('date');
  }

  public updateFilterValue(
    filterType: string,
    filterItem: CollectionItemType
  ): void {
    const filterValueMap = {
      type: 'name',
    };

    let filterValues = [...this.filter[filterType]];
    const value = filterItem[filterValueMap[filterType] || 'id'];

    if (filterValues.includes(value)) {
      filterValues = filterValues.filter((t) => t !== value);
    } else {
      filterValues.push(value);
    }
    this.updateFilter(filterType, filterValues);
    readFirst(this.getCollectionItems(false, true));

    this.trackFiltering(filterType);
  }

  public updateFilterValues(
    filterType: string,
    filterValues: string[]
  ): void {
    this.updateFilter(filterType, filterValues);
    readFirst(this.getCollectionItems(false, true));

    this.trackFiltering(filterType);
  }

  public resetFilterValue(filterType: string): void {
    this.updateFilter(filterType, []);
    readFirst(this.getCollectionItems(false, true));
  }

  public resetFilters(classifier?: string): void {
    this.updateState({
      filter: getFilterEmptyState(
        classifier || this.filter.currentItemClassifier
      ),
      filterToFocus: null,
    });
    readFirst(this.getCollectionItems(false));
  }

  public getInitialCollectionItems(
    updatedState: Partial<ProfileCollectionStoreState>
  ): Observable<boolean> {
    this.updateState(updatedState);
    return this.getCollectionItems(false, true);
  }

  public getCollectionItems(
    append: boolean,
    fromFilterUpdate?: boolean
  ): Observable<boolean> {
    const classifier = this.filter.currentItemClassifier;
    switch (classifier) {
      case 'learning':
      case 'experiences':
      case 'achievements':
        return this.getInputsAndOutcomes(append, fromFilterUpdate);
      case 'pathways':
      case 'plans':
        return this.getPlansAndPathways(classifier, fromFilterUpdate);
    }
  }

  public updateSelectedCollectionItems(
    updatedTags: TagsApi.Tag[]
  ): Observable<void> {
    const items = this.bulkUpdateVM.selectedItems;
    return this._api.updateCollectionItems(items, updatedTags);
  }

  public setCurrentItemClassifier(classifier: string): void {
    this.resetBulkUpdate();
    this.resetFilters(classifier);
    this.trackFiltering('filter tab');
  }

  // move to utils
  public showFilters(): boolean {
    const bulkEditMode = this.bulkUpdateVM.enabled;
    const [hasFilterValues, hasSearchTerm] = this._store.useQuery((s) => [
      s.hasFilterValues,
      s.hasSearchTerm,
      s.isLoading,
    ]);
    const { appending, filterUpdating } = this.status;
    const appendingOrUpdating = appending || filterUpdating;
    const hasItems =
      !!this.items.length || !!hasFilterValues || !!hasSearchTerm;
    return (!bulkEditMode && hasItems) || appendingOrUpdating;
  }

  private updateState(args: Partial<ProfileCollectionStoreState>) {
    const updateState = (prevState: ProfileCollectionStoreState) => ({
      ...prevState,
      ...args,
    });

    this._store.update(updateState);
  }

  private addComputedProperties(
    state: ProfileCollectionStoreState
  ): ProfileCollectionState {
    this.translateService.instant('dgProfileOverview_NonOwnerEmptyStateBody', {
      firstName: this.owner.name.split(' ')[0],
    });
    const { inputTypes, experienceTypes, outcomeTypes } = getItemTypes(
      state.processedFacets.itemTypes
    );

    return {
      ...state,
      pathwaysOrPlansDisplayed: ['pathways', 'plans'].includes(
        state.filter.currentItemClassifier
      ),
      showExperienceTypes: this.ldFlagsService.showExperienceTypes,
      hasFilterValues: this.hasFilterValues,
      hasSearchTerm: !!state.filter.term,
      placeholderTiles: getEmptyTileCount(state.items.length, TILE_ROW_LIMIT),
      addKeys: this.profileCollectionUtilsService.getAddKeys(
        this.filter.currentItemClassifier
      ),
      tileRowLimit: TILE_ROW_LIMIT,
      inputTypes,
      experienceTypes,
      outcomeTypes,
      nonOwnerEmptyStateText: this.translateService.instant(
        'dgProfileOverview_NonOwnerEmptyStateBody',
        {
          firstName: this.owner.name.split(' ')[0],
        }
      ),
      sortKeys: this.profileCollectionUtilsService.sortKeys,
      clearButtonsCopy: {
        tags: this.profileCollectionUtilsService.getClearFilterButtonText(
          state.filter.tags.length
        ),
        providers: this.profileCollectionUtilsService.getClearFilterButtonText(
          state.filter.providers.length
        ),
        pathwayStatus:
          this.profileCollectionUtilsService.getClearFilterButtonText(
            state.filter.pathwayStatus.length
          ),
        targetStatus:
          this.profileCollectionUtilsService.getClearFilterButtonText(
            state.filter.targetStatus.length
          ),
        type: this.profileCollectionUtilsService.getClearFilterButtonText(
          state.filter.type.length
        ),
        experienceType:
          this.profileCollectionUtilsService.getClearFilterButtonText(
            state.filter.experienceType.length
          ),
      },
      typeFilterConfig: this.profileCollectionUtilsService.getTypeFilterConfig(
        inputTypes,
        experienceTypes,
        outcomeTypes,
        state.filter.currentItemClassifier
      ),
    };
  }

  private addViewModelAPI(
    state: ProfileCollectionState
  ): ProfileCollectionViewModel {
    const api = {
      getEmptyTileCount,
      getEmptyStateTitle:
        this.profileCollectionUtilsService.getEmptyStateTitle.bind(
          this.profileCollectionUtilsService
        ),
      isItemSelected,
      getCollectionItems: this.getCollectionItems.bind(this),
      showFilters: this.showFilters.bind(this),
      isPlanOrPathwayTile,
      isOutcomeTile,
      isExperienceTile,
      isInputTile,
      toggleItemSelection: this.toggleItemSelection.bind(this),
      resetFilters: this.resetFilters.bind(this),
      toggleBulkEditMode: this.toggleBulkEditMode.bind(this),
    };

    return { ...api, ...state };
  }

  private getInputsAndOutcomes = (
    append: boolean,
    fromFilterUpdate?: boolean
  ): Observable<boolean> => {
    return new Observable((subscriber) => {
      this.updateStatus({
        loading: true,
        appending: append,
        filterUpdating: fromFilterUpdate,
      });
      const filter = { ...this.filter };
      if (!filter.type?.length) {
        filter.type = ITEM_TYPE_MAP[this.filter.currentItemClassifier];
      }

      const next = (collectionItems: CollectionItem<any>) => {
        this._hasError$.next(false);
        const { items, processedFacets, hasMoreItems } = collectionItems;
        let allItems = items;
        if (append) {
          allItems = [...this.items, ...items];
        }
        // TODO: Temporary hack to move experience type dropdown ahead of the skill facet dropdown(Requirement based on comments of PD-93383)
        if (this.filter.currentItemClassifier === 'experiences') {
          processedFacets.facets = processedFacets.facets.reverse();
        }
        this.updateState({
          hasMoreItems,
          processedFacets,
          items: allItems,
          fromFilterUpdate,
          ...getItemTypes(processedFacets.itemTypes),
          displayFacets: getDisplayFacets(processedFacets.facets),
          placeholderTiles: getEmptyTileCount(allItems.length, TILE_ROW_LIMIT),
          indexesOfCardsToExpand: [],
        });
      };

      const error = (err: HttpErrorResponse) => {
        // this._store.handleError({});
        this._hasError$.next(true);
        subscriber.error(err);
      };

      const complete = () => {
        this.updateStatus();
      };

      this._api
        .getCollectionItems(filter, this.owner.id, append)
        .pipe(this._store.trackLoadStatus()) //  delayAtLeast(450)
        .subscribe({ next, error, complete });
    });
  };

  private getPlansAndPathways = (
    contentType: string,
    fromFilterUpdate?: boolean
  ): Observable<boolean> => {
    return new Observable((subscriber) => {
      // no pagination for plans or pathways yet
      this.updateStatus({
        loading: true,
        appending: false,
        filterUpdating: fromFilterUpdate,
      });
      const api =
        contentType === 'plans'
          ? this._api.getPlansAsCollectionItems.bind(this._api)
          : this._api.getPathwaysAsCollectionItems.bind(this._api);

      const next = ({ items, processedFacets }) => {
        this._hasError$.next(false);
        this.updateState({
          items,
          processedFacets,
          displayFacets: getDisplayFacets(processedFacets.facets),
          hasMoreItems: false,
          fromFilterUpdate,
          placeholderTiles: getEmptyTileCount(items.length, TILE_ROW_LIMIT),
          indexesOfCardsToExpand: [],
        });
      };

      const error = (err: HttpErrorResponse) => {
        console.error(error);
        this._hasError$.next(true);
        subscriber.error(err);
      };

      const complete = () => {
        this.updateStatus();
      };

      api(
        this.owner.id,
        this.filter,
        this._store.useQuery(
          (s) => s.sorting || this.profileCollectionUtilsService.sortKeys[0]
        )
      )
        .pipe(this._store.trackLoadStatus()) //  delayAtLeast(450)
        .subscribe({ next, error, complete });
    });
  };

  private updateFilter(
    filterType: string,
    filterValues: string | string[]
  ): void {
    const newFilter = {
      ...this.filter,
      [filterType]: filterValues,
    };
    this.updateState({ filter: newFilter, filterToFocus: filterType });
    // this.onFiltersChanged();
  }

  private updateStatus(args?: {
    appending?: boolean;
    filterUpdating?: boolean;
    loading: boolean;
  }): void {
    const newStatus = args
      ? { ...this.status, ...args }
      : { appending: false, filterUpdating: false, loading: false };
    this.updateState({ status: newStatus });
  }

  private trackFiltering(filterType: string): void {
    const idMap = {
      tags: 'Skills',
      providers: 'Provider',
      experienceType: 'Experience Type',
      type: 'Type',
      date: 'Date',
    };
    let filterLabel: string;
    let startDate = null;
    let endDate = null;
    const formatDate = (date: string) =>
      !!date ? this.datePipe.transform(date, 'd MMM y') : undefined;

    switch (filterType) {
      case 'tags':
        // get the last added tag
        filterLabel = this.filter.tags.slice(-1)[0];
        break;
      case 'providers':
        // get the last added provider
        filterLabel = this.filter.providers.slice(-1)[0];
        break;
      case 'experienceType':
        // get the last added provider
        filterLabel = this.filter.experienceType.slice(-1)[0];
        break;
      case 'date':
        filterLabel = 'dates';
        startDate = formatDate(this.filter.startDate);
        endDate = formatDate(this.filter.endDate);
        break;
      case 'type':
        filterLabel = this.filter.type.join(',');
        break;
      default:
        filterLabel = filterType;
        break;
    }

    this.trackerService.trackEventData({
      action: 'Profile Collection Filtered',
      properties: {
        filterType: idMap[filterType],
        StartDate: startDate,
        EndDate: endDate,
        FilterTabClicked: this.filter.currentItemClassifier,
      },
    });
  }

  private trackSearch(): void {
    const { term } = this.filter;
    const searchTerm = term ? term.toLowerCase() : '';
    this.trackerService.trackEventData({
      action: 'Profile Collection Searched',
      properties: { Keywords: searchTerm },
    });
  }
}
